'use client'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useFileUpload } from '@/lib/upload/useFileUpload'
import { queryKeys } from '@/lib/queryKeys'
import {
  fetchPublicCaseTracking,
  fetchPublicUploadLinkInfo,
  trackByReference,
} from '@/features/public/api/public.api'
import type { PublicUploadDocumentResult } from '@/features/public/types'

export function usePublicCaseTracking(trackingToken: string) {
  return useQuery({
    queryKey: queryKeys.public.tracking(trackingToken),
    queryFn: () => fetchPublicCaseTracking(trackingToken),
  })
}

export function usePublicUploadLinkInfo(token: string) {
  return useQuery({
    queryKey: queryKeys.public.uploadLink(token),
    queryFn: () => fetchPublicUploadLinkInfo(token),
  })
}

/** "Track My Status" — no `onError` toast, same reasoning as `useSubmitPublicCase`: a mismatch
 * surfaces inline in the dialog itself, not a transient toast. */
export function useTrackByReference() {
  return useMutation({ mutationFn: trackByReference })
}

/**
 * The app's second unauthenticated write (after Phase 11a's `useSubmitPublicCase`) — bypasses
 * `useApiFetch()` (no Clerk session to attach), reuses `useFileUpload()` directly, and sends an
 * `Idempotency-Key` per the backend's documented support for this endpoint.
 */
export function useUploadPublicDocument(token: string) {
  const { upload, progress, isUploading } = useFileUpload()
  const mutation = useMutation({
    mutationFn: (input: { file: File; kind: 'cas_pdf' | 'broker_xlsx'; password?: string }) => {
      const formData = new FormData()
      formData.append('file', input.file)
      formData.append('kind', input.kind)
      if (input.password) formData.append('password', input.password)
      return upload<PublicUploadDocumentResult>(
        `/api/v1/public/upload-links/${token}/documents`,
        formData,
        { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      )
    },
  })
  return { ...mutation, progress, isUploading }
}
